import { Lock, Shield, Server, FileWarning, Users, Eye, Database, GitBranch } from "lucide-react";

const CONTROLS = [
  { icon: Lock, title: "Encryption in Transit & At Rest", desc: "TLS 1.2+ transport; storage encryption for documents and database fields containing PHI/PII." },
  { icon: Users, title: "Role-Based Access & Least Privilege", desc: "Role separation across admin, staff RN, attorney/VA-accredited, and client scopes." },
  { icon: Shield, title: "MFA-Ready Authentication", desc: "JWT-based authentication with an MFA-ready schema for step-up requirements." },
  { icon: Database, title: "Data Segregation", desc: "Separate handling of PHI, PII, VA-related information, and potentially CUI." },
  { icon: Eye, title: "Comprehensive Audit Logs", desc: "Immutable audit trail of authentication, access, uploads, downloads, and mutations." },
  { icon: Server, title: "Federal-Cloud-Ready Architecture", desc: "Deployment path capable of operating in appropriately authorized federal cloud services." },
  { icon: FileWarning, title: "Incident Response & Review", desc: "Documented response procedures with post-incident review and corrective-action tracking." },
  { icon: GitBranch, title: "Retention & Disposal Controls", desc: "Configurable retention with deliberate disposal aligned to contractual obligations." },
];

const MAPPING = [
  { framework: "HIPAA Security Rule", controls: "Access control, audit controls, integrity, transmission security" },
  { framework: "NIST 800-171 Rev. 3", controls: "Access control, audit & accountability, identification & authentication, media protection, system & communications protection" },
  { framework: "CMMC (Draft Alignment)", controls: "Level 2 practices where CUI is present" },
  { framework: "CUI Handling", controls: "Marking, safeguarding, transmission, and destruction procedures" },
  { framework: "DOD Requirements", controls: "Contract-driven; mapped where the engagement dictates" },
];

export default function Security() {
  return (
    <div className="fade-in-up">
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="text-caption text-[#B91C1C] mb-4">SECURITY & COMPLIANCE</div>
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-[#0A192F] max-w-3xl leading-tight">The highest-security path — designed from day one.</h1>
          <p className="text-slate-600 max-w-3xl mt-5 text-lg leading-relaxed">
            The items below are our engineering design targets. Final control mappings and authorizations are validated per engagement against your contract, data types, hosting authorization, and legal/security review.
          </p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {CONTROLS.map((c, i) => (
            <div key={c.title} className="card-federal p-6" data-testid={`control-${i}`}>
              <c.icon className="w-6 h-6 text-[#0A192F] mb-4" strokeWidth={1.6} />
              <h3 className="font-serif font-bold text-[#0A192F] mb-2 leading-snug">{c.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold mb-3">Design-target framework mapping</h2>
          <p className="text-slate-300 mb-10 max-w-2xl">Not certifications — engineering intent, subject to validation.</p>
          <div className="border border-white/15">
            <table className="w-full text-sm">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left px-6 py-4 text-caption text-white/70">Framework</th>
                  <th className="text-left px-6 py-4 text-caption text-white/70">Control focus</th>
                </tr>
              </thead>
              <tbody>
                {MAPPING.map((m) => (
                  <tr key={m.framework} className="border-t border-white/10">
                    <td className="px-6 py-4 font-serif font-bold">{m.framework}</td>
                    <td className="px-6 py-4 text-slate-300">{m.controls}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[12px] text-slate-400 mt-6 font-mono">DISCLAIMER — DESIGN TARGETS REQUIRING VALIDATION AGAINST CONTRACTS, DATA TYPES, HOSTING AUTHORIZATION, AND LEGAL/SECURITY REVIEW.</p>
        </div>
      </section>
    </div>
  );
}
